import { makeStyles } from '@material-ui/core/styles';

import { COLORS } from 'src/data/ui';

export const useStyles = makeStyles(
  (theme) => ({
    root: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
      flexFlow: 'row nowrap',
      alignItems: 'center',

      transitionProperty: (props) => (props.transition ? 'background-color' : 'unset'),
      transitionDuration: (props) => (props.transition ? '6s' : 'unset'),

      '&$isDraft': {
        alignItems: 'unset !important',
        padding: theme.spacing(2, 3, 2, 3),
      },

      '&$highlighted': {
        backgroundColor: COLORS.primary.silver,
      },

      border: theme.shape.borderGray60,
      padding: theme.spacing(3, 3, 4, 3),

      // CSS hovered state
      '&$selected': {
        backgroundColor: COLORS.gray.background,
        transitionProperty: 'unset',
        transitionDuration: 'unset',

        '&$detailsAllowed': {
          backgroundColor: COLORS.gray.background,

          '& $score': {
            backgroundColor: COLORS.gray.background,
          },
        },
      },

      '&$counterInvalid:not($editing):not($displayActions)': {
        padding: theme.spacing(2, 3, 4, 3),
        '& $textCounter': {
          opacity: '1',
          bottom: 10,
        },
      },

      // Logical "editing" state
      '&$editing': {
        '&$empty': {
          '& $textCounter': {
            opacity: '0',
          },
        },

        '& $textCounter': {
          opacity: '1',
        },

        '&$hasEditingControls $editorWrapper': {
          backgroundColor: COLORS.gray.gray90,
          padding: theme.spacing(4, 2, 7.5),
          margin: theme.spacing(-2, -12.5, -3, -2),
          borderRadius: theme.shape.borderRadiusSemiLarge,
          borderColor: COLORS.neutral.white,
        },
      },

      // State when component was requested to display actions and it's not in "editing" state
      '&$displayEditorActions:not($editing), &$displayEditorActions$actionsVisibleOnEdit': {
        padding: theme.spacing(2, 3, 10, 3),
        '& $textCounter': {
          opacity: '1',
          zIndex: 1000,
          top: 'unset',
          bottom: 58,
        },
      },

      '&$displayActions:not($editing), &$displayActions$actionsVisibleOnEdit': {
        '& $actions': {
          opacity: 1,
          transition: theme.transitions.create('opacity', {
            duration: theme.transitions.duration.short,
          }),
          pointerEvents: 'auto',
        },
      },

      '&$detailsAllowed': {
        cursor: 'pointer',

        '& $score': {
          marginRight: theme.spacing(-1),
        },
      },

      '&$extraHeight': {
        minHeight: 102,
      },

      '&$withStatusHeight': {
        minHeight: 112,
      },

      '&$withStatusHeightLocked': {
        minHeight: 132,
      },
    },
    alignItemsStart: {
      alignItems: 'start',
    },
    editing: {},
    selected: {},
    empty: {},
    displayScore: {},
    displayActions: {},
    displayEditorActions: {},
    detailsAllowed: {},
    hasEditingControls: {},
    actionsVisibleOnEdit: {},
    textCounter: {
      position: 'absolute',
      top: 8,

      fontSize: theme.typography.pxToRem(12),
      lineHeight: theme.typography.pxToRem(12),
      color: theme.palette.text.secondary,

      opacity: '0',

      '$hasEditingControls &': {
        top: 'unset',
        bottom: 24,
      },
    },
    editorWrapper: {
      position: 'relative',
      border: '1px solid transparent',
      width: '100%',
      display: 'flex',
      justifyContent: 'start',
      alignItems: 'start',
    },
    editor: {
      justifyContent: 'center',
      fontSize: theme.typography.pxToRem(16),
      lineHeight: theme.typography.pxToRem(24),
      wordBreak: 'break-word',
    },
    editorSmall: {
      justifyContent: 'center',
      fontSize: theme.typography.pxToRem(14),
      lineHeight: theme.typography.pxToRem(20),
      wordBreak: 'break-word',
    },
    editWrapper: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    editIcon: {
      color: COLORS.gray.gray40,
      fontSize: theme.typography.pxToRem(16),
    },
    editable: {
      cursor: 'text',
    },

    score: {
      height: '100%',
      flexShrink: '0',
      marginLeft: theme.spacing(2),
      backgroundColor: 'transparent',
    },
    lockedButtons: {
      marginRight: theme.spacing(-1.75),
      marginLeft: theme.spacing(3),
      position: 'relative',
    },
    leftActions: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: '0',
    },
    actions: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      bottom: theme.spacing(1.5),
      left: theme.spacing(3),

      opacity: 0,
      pointerEvents: 'none',
    },
    fullWidthActions: {
      right: theme.spacing(3),
    },
    editingActions: {
      position: 'absolute',
      right: theme.spacing(2),
      bottom: theme.spacing(1),
      fontSize: theme.typography.pxToRem(14),
      '&:hover': {
        backgroundColor: '#ECF2FF',
      },
    },
    editButton: {
      marginLeft: '4px',
      padding: theme.spacing(0.75, 2),
      borderRadius: 4,
      '&:hover': {
        backgroundColor: '#0C5CEF',
      },
    },
    variationContent: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    errorTooltip: {
      marginBottom: '5px',
    },
    labelSaved: {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.secondary.main,
      width: '75px',
    },
    description: {
      fontSize: theme.typography.pxToRem(12),
      color: COLORS.gray.gray40,
    },
    leftOutsideHoverActions: {
      position: 'absolute',
      left: theme.spacing(-3.375),
    },
    rightOutsideHoverActions: {
      position: 'absolute',
      right: theme.spacing(-3.375),
    },
    selectGenerationTool: {
      margin: theme.spacing(0, -1, 0, 3),
      justifyContent: 'flex-end',
    },
    skeleton: {
      width: 35,
      height: 35,
      marginRight: theme.spacing(-1),
    },
    toolBadge: {
      width: 20,
      height: 20,
      position: 'absolute',
      right: 7,
      top: 16,
      background: 'white',
      borderRadius: '50%',
    },
    error: {},
    extraRightPadding: {},
    extraHeight: {},
    withStatusHeight: {},
    withStatusHeightLocked: {},
    isDraft: {},
    highlighted: {},
    notHighlighted: {},

    imageDescriptionTitle: {
      fontSize: theme.typography.pxToRem(12),
      color: COLORS.gray.gray40,
      paddingBottom: theme.spacing(0.5),
    },
    counterInvalid: {},

    originalTag: {
      backgroundColor: COLORS.neutral.mediumGray,
      color: COLORS.neutral.white,
      padding: theme.spacing(0.25, 0.5),
      borderRadius: 4,
      fontSize: theme.typography.pxToRem(11),
      lineHeight: theme.typography.pxToRem(14),
      marginRight: theme.spacing(1),
      fontWeight: theme.typography.fontWeightMedium,
    },
    descriptionWrapper: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      color: COLORS.gray.gray50,
    },
  }),
  { name: 'Variation' }
);
